import { useCallback, useEffect, useRef, useState } from 'react';
import { updateServiceRequestStatus } from '../api/updateServiceRequestStatusApiService';
import {
  UPDATE_SR_CANCELLATION_REASONS,
  UPDATE_SR_STATUS_LABELS as L,
  type TechnicianMissedVisitReasonId,
  type UpdateSrCancellationReasonId,
} from '../constants/labels';
import { UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES } from '../errors';
import {
  getAdminAssigneeAllowedNextStatuses,
  getAllowedNextStatuses,
} from '../pages/viewServiceRequest/viewServiceRequestHelpers';
import { isCancellableNow } from '../utils/cancellationPolicy';
import type { ServiceRequestStatus } from '../types/serviceRequestListType';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/**
 * Step navigation for the status-update modal:
 * - `form`        — pick the new status (+ optional notes / notify).
 * - `cancel-reason` — mandatory cancellation reason (target `cancelled`).
 * - `missed-visit`  — mandatory missed-visit reason + comment (target
 *   `unfulfilled`); only reachable in the admin-assignee flow (RR-916).
 */
export type UpdateSrStep = 'form' | 'cancel-reason' | 'missed-visit';

interface UseUpdateServiceRequestStatusOptions {
  serviceRequestId: string | undefined;
  currentStatus: ServiceRequestStatus | undefined;
  /**
   * Scheduled start (UTC ISO). Used to enforce the FE 24-hour cancel gate —
   * when the SR is `assigned` and the visit is less than 24h away, the
   * `cancelled` option drops out of the allowed-status list and the
   * cancel CTA is suppressed.
   */
  scheduledStartAt?: string | null | undefined;
  assigneeMode?: boolean;
  /** Receives the status that was just submitted, so the caller can pick a status-specific success message (e.g. "cancelled" vs generic). */
  onSuccess?: (submittedStatus: ServiceRequestStatus) => void;
}

export interface UseUpdateServiceRequestStatusReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;

  // Step
  step: UpdateSrStep;
  goToCancelReasonStep: () => void;
  goBackToForm: () => void;

  newStatus: ServiceRequestStatus | '';
  setNewStatus: (status: ServiceRequestStatus) => void;
  adminNotes: string;
  setAdminNotes: (value: string) => void;
  notify: boolean;
  setNotify: (value: boolean) => void;
  newStatusError: string;
  adminNotesError: string;

  // Cancel-reason step
  cancellationReason: UpdateSrCancellationReasonId | '';
  setCancellationReason: (id: UpdateSrCancellationReasonId) => void;
  customReason: string;
  setCustomReason: (value: string) => void;
  cancellationReasonError: string;
  customReasonError: string;

  // `adminNotes`; only the reason needs its own state.
  missedVisitReason: TechnicianMissedVisitReasonId | '';
  setMissedVisitReason: (id: TechnicianMissedVisitReasonId) => void;
  missedVisitReasonError: string;

  isSubmitting: boolean;
  submitError: string;
  clearSubmitError: () => void;
  allowedStatuses: ServiceRequestStatus[];
  /** True when the current step has a valid payload that the user can submit. */
  canSubmit: boolean;
  /** True when step 1 has been completed enough to advance to the cancel-reason step. */
  canContinue: boolean;
  submit: () => Promise<void>;
}

export const useUpdateServiceRequestStatus = ({
  serviceRequestId,
  currentStatus,
  scheduledStartAt,
  assigneeMode = false,
  onSuccess,
}: UseUpdateServiceRequestStatusOptions): UseUpdateServiceRequestStatusReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<UpdateSrStep>('form');
  const [newStatus, setNewStatusState] = useState<ServiceRequestStatus | ''>(
    ''
  );
  const [adminNotes, setAdminNotesState] = useState('');
  const [notify, setNotify] = useState(false);
  const [newStatusError, setNewStatusError] = useState('');
  const [adminNotesError, setAdminNotesError] = useState('');

  const [cancellationReason, setCancellationReasonState] = useState<
    UpdateSrCancellationReasonId | ''
  >('');
  const [customReason, setCustomReasonState] = useState('');
  const [cancellationReasonError, setCancellationReasonError] = useState('');
  const [customReasonError, setCustomReasonError] = useState('');

  const [missedVisitReason, setMissedVisitReasonState] = useState<
    TechnicianMissedVisitReasonId | ''
  >('');
  const [missedVisitReasonError, setMissedVisitReasonError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [submitError, setSubmitError] = useState('');

  let allowedStatuses: ServiceRequestStatus[];
  if (assigneeMode) {
    const startMs = scheduledStartAt ? Date.parse(scheduledStartAt) : NaN;
    const hasStart = !Number.isNaN(startMs);
    const scheduledStartPassed = !hasStart || Date.now() >= startMs;
    const visitWithin24h =
      hasStart && startMs - Date.now() < TWENTY_FOUR_HOURS_MS;
    allowedStatuses = (
      currentStatus ? getAdminAssigneeAllowedNextStatuses(currentStatus) : []
    ).filter((s) => {
      if (s === 'in_progress') {
        return scheduledStartPassed;
      }
      if (s === 'cancelled') {
        return !visitWithin24h;
      }
      return true;
    });
  } else {
    const allTransitions = currentStatus
      ? getAllowedNextStatuses(currentStatus)
      : [];
    // Hide 'cancelled' from the dropdown when the FE 24h cancel gate is closed
    // (e.g. assigned SR with <24h to visit). BE remains the authoritative gate.
    const cancelEligibility = isCancellableNow(currentStatus, scheduledStartAt);
    allowedStatuses = cancelEligibility.canCancel
      ? allTransitions
      : allTransitions.filter((s) => s !== 'cancelled');
  }

  const resetForm = useCallback(() => {
    setStep('form');
    setNewStatusState('');
    setAdminNotesState('');
    setNotify(false);
    setNewStatusError('');
    setAdminNotesError('');
    setCancellationReasonState('');
    setCustomReasonState('');
    setCancellationReasonError('');
    setCustomReasonError('');
    setMissedVisitReasonState('');
    setMissedVisitReasonError('');
    setSubmitError('');
  }, []);

  const open = useCallback(() => {
    resetForm();
    setIsOpen(true);
  }, [resetForm]);

  const close = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    resetForm();
  }, [isSubmitting, resetForm]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetForm();
  }, [serviceRequestId, currentStatus, isOpen, resetForm]);

  const setNewStatus = useCallback((status: ServiceRequestStatus) => {
    setNewStatusState(status);
    setNewStatusError('');
    // Switching off 'cancelled' resets any cancel-step state so the user
    // doesn't accidentally carry over a stale reason.
    if (status !== 'cancelled') {
      setCancellationReasonState('');
      setCustomReasonState('');
      setCancellationReasonError('');
      setCustomReasonError('');
    } else {
      // Admin notes don't apply when cancelling — clear the field so the
      // submit payload is clean if the user toggled back and forth.
      setAdminNotesState('');
      setAdminNotesError('');
    }
    // Same for the missed-visit reason (RR-916): only `unfulfilled` uses it.
    // The comment reuses `adminNotes`, which is reset on open / step entry.
    if (status !== 'unfulfilled') {
      setMissedVisitReasonState('');
      setMissedVisitReasonError('');
    }
  }, []);

  const setAdminNotes = useCallback((value: string) => {
    setAdminNotesState(value);
    if (value.trim().length <= L.NOTES_MAX_LENGTH) {
      setAdminNotesError('');
    }
  }, []);

  const setCancellationReason = useCallback(
    (id: UpdateSrCancellationReasonId) => {
      setCancellationReasonState(id);
      setCancellationReasonError('');
      // Picking a non-Other option clears the custom text so it doesn't
      // get submitted by accident.
      if (id !== 'other') {
        setCustomReasonState('');
        setCustomReasonError('');
      }
    },
    []
  );

  const setCustomReason = useCallback((value: string) => {
    setCustomReasonState(value);
    if (value.trim().length <= L.NOTES_MAX_LENGTH) {
      setCustomReasonError('');
    }
  }, []);

  const setMissedVisitReason = useCallback(
    (id: TechnicianMissedVisitReasonId) => {
      setMissedVisitReasonState(id);
      setMissedVisitReasonError('');
      // The comment (adminNotes) is only mandatory for the "Other" reason;
      // clear a stale required-comment error when a preset reason is picked.
      if (id !== 'other') {
        setAdminNotesError('');
      }
    },
    []
  );

  const clearSubmitError = useCallback(() => setSubmitError(''), []);

  const isCancelling = newStatus === 'cancelled';
  const isUnfulfilled = newStatus === 'unfulfilled';
  // Targets that collect extra input on a dedicated second step rather than
  // submitting straight from the form: `cancelled` (reason) and, in the
  // admin-assignee flow, `unfulfilled` (missed-visit reason + comment).
  const needsDetailStep = isCancelling || isUnfulfilled;

  // Step 1's Continue / Update Status button stays enabled so the user can
  // click it and surface the required-field error on the status dropdown.
  // Only the in-flight state disables it.
  const canContinue = !isSubmitting;

  const canSubmit =
    step === 'form' ? (needsDetailStep ? false : !isSubmitting) : !isSubmitting;

  const goToCancelReasonStep = useCallback(() => {
    if (!newStatus) {
      setNewStatusError(L.ERROR_REQUIRED);
      return;
    }
    // Route to the target's detail step: `cancelled` → cancel-reason,
    // `unfulfilled` → missed-visit (RR-916). Every other target submits
    // straight from the form, so there is nothing to advance to.
    if (newStatus === 'cancelled') {
      setStep('cancel-reason');
      setSubmitError('');
    } else if (newStatus === 'unfulfilled') {
      setStep('missed-visit');
      setSubmitError('');
    }
  }, [newStatus]);

  const goBackToForm = useCallback(() => {
    setStep('form');
    setSubmitError('');
  }, []);

  const submit = useCallback(async () => {
    if (!serviceRequestId || isSubmittingRef.current) {
      return;
    }
    // Step-aware validation. Both branches return early on failure so the
    // common API call below only runs once everything is valid.
    let hasError = false;
    if (step === 'cancel-reason') {
      if (!cancellationReason) {
        setCancellationReasonError(L.ERROR_REQUIRED);
        hasError = true;
      }
      if (cancellationReason === 'other' && customReason.trim().length === 0) {
        setCustomReasonError(L.ERROR_REQUIRED);
        hasError = true;
      }
      if (customReason.trim().length > L.NOTES_MAX_LENGTH) {
        setCustomReasonError(L.ERROR_REASON_TOO_LONG);
        hasError = true;
      }
    } else if (step === 'missed-visit') {
      if (!missedVisitReason) {
        setMissedVisitReasonError(L.ERROR_REQUIRED);
        hasError = true;
      }
      if (missedVisitReason === 'other' && adminNotes.trim().length === 0) {
        setAdminNotesError(L.ERROR_REQUIRED);
        hasError = true;
      }
      if (adminNotes.trim().length > L.NOTES_MAX_LENGTH) {
        setAdminNotesError(L.ERROR_NOTES_TOO_LONG);
        hasError = true;
      }
    } else {
      if (!newStatus) {
        setNewStatusError(L.ERROR_REQUIRED);
        hasError = true;
      }
      if (adminNotes.trim().length > L.NOTES_MAX_LENGTH) {
        setAdminNotesError(L.ERROR_NOTES_TOO_LONG);
        hasError = true;
      }
    }
    if (hasError) {
      return;
    }
    // On the cancel-reason step `newStatus` is already 'cancelled' (set on
    // step 1) and `adminNotes` was cleared when the user picked cancel.
    // The cancellation reason itself lives in local state only — once the
    // BE adds a `cancellation_reason` field, send it here.

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');

    // For cancellation the BE requires `admin_notes` to carry the
    // cancellation reason (see services/.../service_request.types.ts —
    // superRefine on new_status === CANCELLED). Resolve it from the
    // cancel-step state: free-text when reason === 'other', otherwise the
    // label of the picked preset.
    let payloadAdminNotes = adminNotes;
    if (newStatus === 'cancelled') {
      if (cancellationReason === 'other') {
        payloadAdminNotes = customReason.trim();
      } else if (cancellationReason) {
        const preset = UPDATE_SR_CANCELLATION_REASONS.find(
          (r) => r.id === cancellationReason
        );
        payloadAdminNotes = preset?.label ?? '';
      }
    }

    // Missed-visit : send the structured reason code alongside the
    // comment, and ask the BE to notify (assignee transitions always notify;
    // the BE ignores this flag but we mirror the technician payload). For all
    // other targets the `notify` checkbox value is forwarded as-is.
    const isMissedVisit = newStatus === 'unfulfilled';

    try {
      const result = await updateServiceRequestStatus({
        serviceRequestId,
        newStatus: newStatus as ServiceRequestStatus,
        adminNotes: payloadAdminNotes,
        notify: isMissedVisit ? true : notify,
        ...(isMissedVisit && missedVisitReason ? { missedVisitReason } : {}),
      });
      if (result.success) {
        const submittedStatus = newStatus as ServiceRequestStatus;
        setIsOpen(false);
        resetForm();
        onSuccess?.(submittedStatus);
      } else {
        setSubmitError(
          result.error ??
            UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.DEFAULT_ERROR
        );
      }
    } catch {
      setSubmitError(
        UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.UNEXPECTED_ERROR
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    serviceRequestId,
    step,
    newStatus,
    adminNotes,
    notify,
    cancellationReason,
    customReason,
    missedVisitReason,
    onSuccess,
    resetForm,
  ]);

  return {
    isOpen,
    open,
    close,
    step,
    goToCancelReasonStep,
    goBackToForm,
    newStatus,
    setNewStatus,
    adminNotes,
    setAdminNotes,
    notify,
    setNotify,
    newStatusError,
    adminNotesError,
    cancellationReason,
    setCancellationReason,
    customReason,
    setCustomReason,
    cancellationReasonError,
    customReasonError,
    missedVisitReason,
    setMissedVisitReason,
    missedVisitReasonError,
    isSubmitting,
    submitError,
    clearSubmitError,
    allowedStatuses,
    canSubmit,
    canContinue,
    submit,
  };
};
